(function () {
  'use strict';

  var STORAGE_KEY = 'peak-love-sound';
  var POSITION_KEY = 'peak-love-track-at';
  var MAX_GAP = 5;              // seconds of navigation credited on resume
  var AUDIO_SRC = (typeof window.peakLoveAudioSrc !== 'undefined')
    ? window.peakLoveAudioSrc
    : './assets/audio/peak-love-audio.mp3';

  /* Audio cannot survive a document unload, so on a real page load the track
     would otherwise restart from zero every time. The position is handed over
     instead, advanced by however long the navigation itself took, so the music
     picks up where it would have been rather than where it stopped. */
  function savePosition() {
    if (!audio || swapping) return;
    /* Don't clobber a good handoff with a zero. A page that loads while the
       tab is hidden can fire this before the restore has been applied, which
       would reset the track to the start on the next hop. */
    if (!didRestore) return;
    try {
      sessionStorage.setItem(POSITION_KEY, JSON.stringify({
        t: audio.currentTime || 0,
        at: Date.now(),
        on: isOn,
      }));
    } catch (err) { /* ignore */ }
  }

  function restorePosition() {
    if (!audio) {
      didRestore = true;
      return;
    }
    try { localStorage.removeItem(POSITION_KEY); } catch (err) { /* ignore */ }

    var raw;
    try { raw = sessionStorage.getItem(POSITION_KEY); } catch (err) {
      didRestore = true;
      return;
    }
    if (!raw) {
      didRestore = true;
      return;
    }

    var data;
    try { data = JSON.parse(raw); } catch (err) {
      didRestore = true;
      return;
    }
    if (!data || typeof data.t !== 'number') {
      didRestore = true;
      return;
    }

    /* Credit a short navigation gap so the same half-second isn't replayed
       on every hop. A longer gap means they were away — resume at the saved
       time instead of jumping ahead. Only credit while the track was on,
       so mute + navigate still picks up at the mute point. */
    var elapsed = Math.max(0, (Date.now() - (data.at || Date.now())) / 1000);
    var target = (data.on && elapsed <= MAX_GAP) ? data.t + elapsed : data.t;

    function seek() {
      // Wrap only when the duration is actually known. A server without
      // Range support leaves it Infinity, in which case seek unwrapped
      // rather than not at all.
      var d = audio.duration;
      var pos = (d && isFinite(d) && d > 0) ? (target % d) : target;
      try {
        audio.currentTime = pos;
      } catch (err) { /* seeking may be refused before data is ready */ }
      didRestore = true;
    }

    if (audio.readyState >= 2) seek();
    else {
      audio.addEventListener('loadeddata', seek, { once: true });
      setTimeout(function () { if (!didRestore) seek(); }, 4000);
    }
  }

  /* --------------------------------------------------------------------------
     Wave geometry. The svg is 32x9 user units mapped 1:1 onto 32x9 css px, so
     every constant below reads as pixels.

     AMP is the crest-to-mid distance of the centre line. The old artwork was a
     filled ribbon spanning y 0 -> 8.79; with a 0.7 stroke that works out to an
     amplitude of ~3.8 around the 4.5 midline, which is what we match here.
     -------------------------------------------------------------------------- */
  var WIDTH = 32;
  var HEIGHT = 9;
  var MID = HEIGHT / 2;
  var AMP = 3.8;
  var STROKE = 0.7;      // same thickness as the old flat-line path
  var PERIOD = 8;        // 4 teeth across the 32px window
  var RISE = 0.88;       // fraction of the period spent climbing -> saw tooth
  var SPEED = 7.2;       // px/sec, matches the old 36px-per-5s scroll
  var TAU = 0.11;        // amplitude lerp time constant (~0.5s to settle)

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isOn = readStored();
  var waitingForGesture = false;
  var audio;
  var didRestore = false;
  var swapping = false;

  var paths = [];
  var amp = 0;           // current, lerped
  var phase = 0;
  var rafId = null;
  var lastT = 0;

  function readStored() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch (err) { /* ignore */ }
    return true;
  }

  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, isOn ? '1' : '0');
    } catch (err) { /* ignore */ }
  }

  /* Saw tooth: ramp up over RISE of the period, then drop back down.
     At amp 0 every vertex collapses onto MID, giving a straight line. */
  function buildPath(a, shift) {
    var d = '';
    var first = true;
    var kEnd = Math.ceil((WIDTH + shift) / PERIOD) + 1;

    for (var k = -1; k <= kEnd; k++) {
      var base = k * PERIOD - shift;
      var trough = [base, MID + a];
      var crest = [base + RISE * PERIOD, MID - a];

      d += (first ? 'M' : 'L') + trough[0].toFixed(2) + ' ' + trough[1].toFixed(2);
      first = false;
      d += 'L' + crest[0].toFixed(2) + ' ' + crest[1].toFixed(2);
    }
    return d;
  }

  function render() {
    var d = buildPath(amp, phase % PERIOD);
    for (var i = 0; i < paths.length; i++) paths[i].setAttribute('d', d);
  }

  function tick(now) {
    var dt = lastT ? Math.min((now - lastT) / 1000, 0.05) : 0;
    lastT = now;

    var target = isOn ? AMP : 0;
    amp += (target - amp) * (1 - Math.exp(-dt / TAU));
    if (Math.abs(target - amp) < 0.002) amp = target;

    if (isOn) phase += SPEED * dt;

    render();

    // Keep running while sounding, or while the line is still settling flat.
    if (isOn || amp !== 0) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = null;
      lastT = 0;
    }
  }

  function startLoop() {
    if (prefersReduced) {
      amp = isOn ? AMP : 0;
      phase = 0;
      render();
      return;
    }
    if (rafId === null) {
      lastT = 0;
      rafId = requestAnimationFrame(tick);
    }
  }

  function buildWaveSvg() {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', WIDTH);
    svg.setAttribute('height', HEIGHT);
    svg.setAttribute('viewBox', '0 0 ' + WIDTH + ' ' + HEIGHT);
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');

    var path = document.createElementNS(ns, 'path');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', STROKE);
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.appendChild(path);

    return { svg: svg, path: path };
  }

  function ensureWaves() {
    paths = [];
    document.querySelectorAll('[data-sound-toggle]').forEach(function (btn) {
      var wrap = btn.querySelector('.sound-wave');
      if (!wrap) {
        wrap = document.createElement('span');
        wrap.className = 'sound-wave';
        wrap.setAttribute('aria-hidden', 'true');
        var before = btn.querySelector('.mask-wrap')
          || btn.querySelector('.soundbtn__label')
          || btn.firstChild;
        btn.insertBefore(wrap, before);
      }
      wrap.textContent = '';
      var built = buildWaveSvg();
      wrap.appendChild(built.svg);
      paths.push(built.path);
    });
    render();
  }

  function updateLabels() {
    var desktopLabel = isOn ? 'Sound (On)' : 'Sound (Off)';
    var mobileLabel = isOn ? 'On' : 'Off';

    document.querySelectorAll('[data-sound-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      btn.classList.toggle('is-off', !isOn);

      if (btn.classList.contains('soundbtn')) {
        var span = btn.querySelector('.soundbtn__label')
          || btn.querySelector('span:not(.sound-wave)');
        if (span) span.textContent = mobileLabel;
        return;
      }

      btn.querySelectorAll('.top-mask, .btm-mask').forEach(function (el) {
        el.textContent = desktopLabel;
      });
    });
  }

  function tryPlay() {
    if (!audio || !isOn) return;
    var play = audio.play();
    if (play && play.catch) {
      play.catch(function () {
        waitingForGesture = true;
      });
    }
  }

  function hasBufferedAhead() {
    if (!audio || audio.buffered.length === 0) return false;
    var t = audio.currentTime;
    for (var i = 0; i < audio.buffered.length; i++) {
      if (audio.buffered.start(i) <= t + 0.05 && audio.buffered.end(i) > t + 0.3) {
        return true;
      }
    }
    return false;
  }

  function applyAudio() {
    if (!audio) return;
    if (isOn) tryPlay();
    else audio.pause();
  }

  /* Shopify's asset CDN often serves large mp3s without usable Range
     support. The previous 36s clip fit in the first chunk; this 7MB
     320kbps file does not, so Safari plays a few seconds then sits
     paused at the end of the buffer. Keep-alive restarts it, and a
     blob copy makes looping/seeking local once the file is in. */
  function keepAlive() {
    if (!audio) return;
    audio.addEventListener('waiting', function () {
      audio.addEventListener('canplay', tryPlay, { once: true });
    });
    audio.addEventListener('stalled', function () {
      audio.addEventListener('canplay', tryPlay, { once: true });
    });
    audio.addEventListener('progress', function () {
      if (isOn && audio.paused && hasBufferedAhead()) tryPlay();
    });
    audio.addEventListener('ended', function () {
      if (!isOn) return;
      try { audio.currentTime = 0; } catch (err) { /* ignore */ }
      tryPlay();
    });
    audio.addEventListener('pause', function () {
      if (!isOn || document.hidden) return;
      if (!hasBufferedAhead()) return;
      requestAnimationFrame(function () {
        if (isOn && audio && audio.paused) tryPlay();
      });
    });
  }

  function attachSrc(url) {
    audio.src = url;
    audio.load();
    restorePosition();
    applyAudio();
  }

  function loadAsBlob(src) {
    return fetch(src, { mode: 'cors', credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('audio fetch');
      return res.blob();
    }).then(function (blob) {
      return URL.createObjectURL(blob);
    });
  }

  function setOn(next, animate) {
    if (isOn !== next) savePosition();
    isOn = next;
    persist();
    applyAudio();
    updateLabels();
    if (animate) {
      startLoop();
    } else {
      amp = isOn ? AMP : 0;
      render();
      if (isOn) startLoop();
    }
  }

  function toggle() {
    setOn(!isOn, true);
  }

  function onFirstGesture(e) {
    if (e.target.closest('[data-sound-toggle]')) return;
    if (waitingForGesture && isOn) tryPlay();
    waitingForGesture = false;
    document.removeEventListener('pointerdown', onFirstGesture);
  }

  audio = new Audio();
  audio.loop = true;
  audio.preload = 'auto';
  audio.volume = 0.2;
  keepAlive();
  attachSrc(AUDIO_SRC);

  loadAsBlob(AUDIO_SRC).then(function (blobUrl) {
    function swap() {
      var t = audio.currentTime || 0;
      var shouldPlay = isOn && !audio.paused;
      swapping = true;
      audio.src = blobUrl;
      audio.addEventListener('loadeddata', function () {
        try { audio.currentTime = t; } catch (err) { /* ignore */ }
        swapping = false;
        if (shouldPlay || isOn) tryPlay();
      }, { once: true });
    }
    if (didRestore) swap();
    else audio.addEventListener('loadeddata', swap, { once: true });
  }).catch(function () {
    swapping = false;
    /* CORS or network: keep the CDN src and rely on keep-alive. */
  });

  /* pagehide covers a normal navigation, and fires on bfcache transitions
     where unload does not — but it is not guaranteed (a killed tab, a crash,
     or an embedded viewport that tears the document down without it). Ticking
     the position once a second means the worst case is resuming a second
     early, rather than falling back to a stale position from minutes ago. */
  window.addEventListener('pagehide', savePosition);
  window.addEventListener('pageshow', function () {
    if (isOn) tryPlay();
  });
  setInterval(savePosition, 1000);

  ensureWaves();
  updateLabels();
  setOn(isOn, false);

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sound-toggle]');
    if (!btn) return;
    e.preventDefault();
    toggle();
  });

  document.addEventListener('pointerdown', onFirstGesture);

  // Don't burn frames on a hidden tab. iOS also pauses media when the
  // tab hides; kick playback when it comes back.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      savePosition();
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; lastT = 0; }
    } else if (isOn) {
      startLoop();
      tryPlay();
    }
  });

  window.PeakLoveSound = {
    isOn: function () { return isOn; },
    position: function () { return audio ? audio.currentTime : 0; },
    setOn: function (next) { setOn(!!next, true); },
    toggle: toggle,
  };
})();
